/**
 * Scroll-derived nav state: hide on scroll down / show on scroll up, glass after the hero,
 * the theme under the bar and the active section. Section geometry is measured only on
 * ScrollTrigger refreshes and resizes; the per-scroll path reads cached numbers only.
 */

/** A section's vertical extent in document px (pin spacers included). */
interface SectionRange {
  id: string
  theme: string
  top: number
  bottom: number
}

/** Options read on every update (they can change between updates). */
export interface NavTrackerOptions {
  /** Never hide the bar (reduced motion, menu open, keyboard focus inside). */
  pinned: () => boolean
  /** Section ids that have a link in the nav. */
  linkIds: ReadonlySet<string>
  /** Called when the active linked section changes (`null` = none). */
  onActive: (id: string | null) => void
}

/** What {@link createNavTracker} returns. */
export interface NavTracker {
  /** Re-measures section extents. Call after layout changes. */
  measure: () => void
  /** Applies state for a scroll position (from Lenis' scroll event). */
  update: (scroll: number) => void
}

/** Scroll distance (px) in one direction before the bar hides or returns. */
const HIDE_AFTER = 56
const SHOW_AFTER = 18
/** Near the top the bar always shows. */
const TOP_ZONE = 120

export function createNavTracker(header: HTMLElement, options: NavTrackerOptions): NavTracker {
  let ranges: SectionRange[] = []
  let heroBottom = Number.POSITIVE_INFINITY
  let viewport = window.innerHeight
  let lastScroll = 0
  let travel = 0
  let hidden = false
  let glass = false
  let theme = ''
  let active: string | null | undefined

  const measure = () => {
    const scrollY = window.scrollY
    viewport = window.innerHeight
    const sections = document.querySelectorAll<HTMLElement>(
      'main section[id], main > [data-theme][id], footer[id]',
    )
    ranges = Array.from(sections, (element) => {
      const spacer = element.parentElement
      const box = spacer?.classList.contains('pin-spacer') ? spacer : element
      const rect = box.getBoundingClientRect()
      return {
        id: element.id,
        theme: element.dataset.theme ?? 'light',
        top: rect.top + scrollY,
        bottom: rect.bottom + scrollY,
      }
    })
    const hero = ranges.find((range) => range.id === 'hero')
    heroBottom = hero ? hero.bottom : viewport * 0.6
  }

  const rangeAt = (y: number) => ranges.find((range) => y >= range.top && y < range.bottom)

  const update = (scroll: number) => {
    const delta = scroll - lastScroll
    lastScroll = scroll
    if (delta !== 0 && Math.sign(delta) !== Math.sign(travel)) travel = 0
    travel += delta

    let nextHidden = hidden
    if (options.pinned() || scroll < TOP_ZONE) nextHidden = false
    else if (travel > HIDE_AFTER) nextHidden = true
    else if (travel < -SHOW_AFTER) nextHidden = false
    if (nextHidden !== hidden) {
      hidden = nextHidden
      header.dataset.hidden = String(hidden)
    }

    // Glass once the hero's bottom passes under the bar.
    const nextGlass = scroll + 72 >= heroBottom
    if (nextGlass !== glass) {
      glass = nextGlass
      header.dataset.glass = String(glass)
    }

    const nextTheme = rangeAt(scroll + 36)?.theme ?? 'light'
    if (nextTheme !== theme) {
      theme = nextTheme
      header.dataset.theme = theme
    }

    const current = rangeAt(scroll + viewport * 0.45)?.id ?? null
    const nextActive = current && options.linkIds.has(current) ? current : null
    if (nextActive !== active) {
      active = nextActive
      options.onActive(active)
    }
  }

  return { measure, update }
}
